import React from "react";
import { useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { Text, View, StyleProp, ViewStyle } from "react-native";
import { EStyleSheet } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { CheckmarkIcon } from "../icons/CheckmarkIcon";
import { XIcon } from "../icons/XIcon";

interface OutcomeProps {
    backgroundColor?: StyleProp<ViewStyle>;
    selected: boolean | null;
    isWinner?: boolean;
    topLabelText: string;
    bottomLabelText: string;
    resulted: boolean;
}

export const OutcomeOptionDisplay: React.FC<OutcomeProps> = ({
    backgroundColor,
    isWinner,
    selected,
    topLabelText,
    bottomLabelText,
    resulted,
}) => {
    const styles = useDynamicStyles(createOutcomeStyles);
    const needsWinLossStyling = resulted && isWinner !== undefined;
    if (selected === null) {
        selected = isWinner ?? false;
    }

    let outerBorderStyling = {};
    let infoContainerStyling = {};
    let pickedContainerStyling = {};

    if (needsWinLossStyling) {
        outerBorderStyling = needsWinLossStyling && isWinner ? styles.winningBorder : styles.losingBorder;
        pickedContainerStyling = isWinner ? styles.winningPick : styles.losingPick;
        infoContainerStyling = isWinner ? styles.winningInfoBorder : styles.losingInfoBorder;
    }

    const getIcon = () => {
        const icon = isWinner ? (
            <CheckmarkIcon fill={EStyleSheet.value(GlobalStyles.WINNING_PROP)} />
        ) : (
            <XIcon fill={EStyleSheet.value(GlobalStyles.LOSING_PROP)} />
        );

        return (
            <View style={[styles.iconContainer, isWinner ? styles.winningBorder : styles.losingBorder]}>
                <View style={styles.iconInnerContainer}>{icon}</View>
            </View>
        );
    };

    return (
        <View style={[styles.optionContainer]}>
            {selected && (
                <View style={[styles.pickedContainer, pickedContainerStyling]}>
                    {!needsWinLossStyling && <Text style={styles.picked}>PICKED</Text>}
                </View>
            )}
            <View
                style={[
                    styles.optionInfoContainer,
                    backgroundColor,
                    selected && styles.defaultOuterBorder,
                    selected && outerBorderStyling,
                    selected && styles.noRoundedTop,
                ]}
            >
                <View
                    style={[
                        styles.optionInfoInnerContainer,
                        selected && styles.defaultInnerBorder,
                        selected && infoContainerStyling,
                        selected && styles.noRoundedTop,
                    ]}
                >
                    <Text style={styles.name}>{topLabelText}</Text>
                    <Text style={[styles.odds]}>{bottomLabelText}</Text>
                </View>
            </View>
            {selected && needsWinLossStyling && getIcon()}
        </View>
    );
};

// Oh my...
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createOutcomeStyles = () => {
    return EStyleSheet.create({
        optionContainer: {
            width: "50%",
            height: 66,
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
        },
        winningBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
        },
        losingBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        pickedContainer: {
            width: "100%",
            height: 22,
            alignItems: "center",
            alignContent: "center",
            justifyContent: "center",
            borderTopLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PICKED_BACKGROUND),
            color: () => EStyleSheet.value(GlobalStyles.PICKED_TEXT),
        },
        winningPick: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.WINNING_PROP),
            marginBottom: 0,
        },
        losingPick: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.LOSING_PROP),
        },
        picked: {
            fontSize: 16,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: "inherit",
        },
        optionInfoContainer: {
            width: "100%",
            height: 44,
            alignItems: "center",
            alignContent: "center",
            justifyContent: "center",
            overflow: "hidden",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: "transparent",
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
        },
        optionInfoInnerContainer: {
            width: "100%",
            flexGrow: "1",
            alignItems: "center",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: "transparent",
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
        },
        noRoundedTop: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
        },
        defaultOuterBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.PROP_SELECTED),
        },
        defaultInnerBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
        },
        winningInfoBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
        },
        losingInfoBorder: {
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
        },
        name: {
            fontSize: 18,
            lineHeight: 18,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.PROP_TEXT),
        },
        odds: {
            fontSize: 16,
            lineHeight: 16,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.PROP_TEXT),
        },
        iconContainer: {
            top: -5,
            position: "absolute",
            width: 34,
            height: 26,
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            overflow: "hidden",
        },
        iconInnerContainer: {
            position: "relative",
            height: 22,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_BACKGROUND),
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
        },
    });
};
